import mixpanel from 'mixpanel-browser';
import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { PageNavTrackingEvents } from '@revelio/core';
import type { UploadFileResult } from '@revelio/data-access';

import { useToast } from '../../../hooks/useToast';
import type { ReportFlowDocument } from '../../ReportFlow/report-flow-types';
import type { ParameterSelection } from '../utils/check-datapoint-in-parameter';
import type { ChartExportInfo } from './chart-export-types';
import {
  type ChartSheetData,
  downloadMultiSheetData,
} from './data-download/download-plot-data';
import type { PdfCoverParams } from './data-download/pdf-export/pdf-exporter';
import {
  exportElementToPdfWithResult,
  generatePdfBlob,
} from './data-download/pdf-export/pdf-exporter';
import {
  exportMultipleChartsToPpt,
  type PptCoverInfo,
  showExportToast,
  showUploadToast,
} from './data-download/ppt-export/ppt-exporter';
import {
  type ChartImageRenderer,
  exportFlowReportToPpt,
} from './data-download/ppt-export/ppt-flow-report-exporter';
import {
  exportReportToPpt,
  type ReportExportParams,
} from './data-download/ppt-export/ppt-report-exporter';
import { dataDownloadRegistry } from './data-download/registry';
import { uploadFileToStorage } from './data-upload/upload-file';

const mergeChartExportInfo = (
  prior: ChartExportInfo,
  incoming: Partial<ChartExportInfo>
): ChartExportInfo => {
  const result: ChartExportInfo = { ...prior };
  (Object.keys(incoming) as (keyof ChartExportInfo)[]).forEach((key) => {
    const value = incoming[key];
    if (value === undefined) {
      return;
    }
    Object.assign(result, { [key]: value });
  });
  return result;
};

export type ReportExportMetadata = Omit<ReportExportParams, 'charts'> & {
  flow?: ReportFlowDocument;
  primarySelections?: ParameterSelection[];
};

/**
 * A report's own parameters, then the page's (the data dates and any filter)
 * that the report doesn't already list.
 */
const mergeCoverParameters = (
  reportParameters: { label: string; value: string }[] = [],
  pageParameters: { label: string; value: string }[] = []
) => [
  ...reportParameters,
  ...pageParameters.filter(
    (parameter) =>
      !reportParameters.some((listed) => listed.value === parameter.value)
  ),
];

export type { ChartExportInfo } from './chart-export-types';

interface PageExportContextType {
  registerChart: (id: string, info: ChartExportInfo) => void;
  unregisterChart: (id: string) => void;
  updateChartExportInfo: (
    id: string,
    partial: Partial<ChartExportInfo>
  ) => void;
  clearChartExportInfo: (id: string, fields: (keyof ChartExportInfo)[]) => void;
  updateChartLoadingState: (id: string, isLoading: boolean) => void;
  setPptExportResolver: (fn: (() => ChartExportInfo[]) | null) => void;
  setPptAuxiliaryBlockingCount: (count: number) => void;
  setPageHasTextOrAiCharts: (hasTextOrAi: boolean) => void;
  setReportExportMetadata: (metadata: ReportExportMetadata | null) => void;
  getChartRegistrySnapshot: () => Map<string, ChartExportInfo>;
  pdfExportRef: MutableRefObject<HTMLDivElement | null>;
  /**
   * Names the page's PPT, PDF and data downloads. Set from inside the tab:
   * this provider sits above it, so it can't read the tab's filters itself.
   */
  setDefaultExportFileName: (fileName: string) => void;
  exportAllCharts: (options?: { cover?: PptCoverInfo }) => Promise<void>;
  exportAllChartsToPdf: (options?: { cover?: PdfCoverParams }) => Promise<void>;
  exportAllChartsData: (options?: { cover?: PptCoverInfo }) => void;
  previewAllCharts: (options?: {
    cover?: PptCoverInfo;
  }) => Promise<UploadFileResult | null>;
  previewAllChartsPdf: (options?: {
    cover?: PdfCoverParams;
  }) => Promise<string | null>;
  setFlowReportPrinter: (printer: (() => Promise<void>) | null) => void;
  printFlowReport: () => Promise<void>;
  setFlowReportChartRenderer: (renderer: ChartImageRenderer | null) => void;
  isExporting: boolean;
  isPageDataReady: boolean;
  waitForPageDataReady: (options?: { quiet?: boolean }) => Promise<void>;
}

const PAGE_DATA_READY_TIMEOUT_MS = 20_000;

export const PageExportContext = createContext<
  PageExportContextType | undefined
>(undefined);

export const PageExportProvider = ({ children }: { children: ReactNode }) => {
  const chartRegistry = useRef<Map<string, ChartExportInfo>>(new Map());
  const loadingStateRef = useRef<Map<string, boolean>>(new Map());
  const [loadingCount, setLoadingCount] = useState(0);
  const [unregisteredPendingCount, setUnregisteredPendingCount] = useState(0);
  const [hasRegisteredCharts, setHasRegisteredCharts] = useState(false);
  const [pageHasTextOrAiCharts, setPageHasTextOrAiCharts] = useState(false);
  const [pptAuxiliaryBlockingCount, setPptAuxiliaryBlockingCount] = useState(0);
  const pptExportResolverRef = useRef<(() => ChartExportInfo[]) | null>(null);
  const reportMetadataRef = useRef<ReportExportMetadata | null>(null);
  const pdfExportElementRef = useRef<HTMLDivElement | null>(null);
  const defaultExportFileNameRef = useRef<string>('');
  const flowReportPrinterRef = useRef<(() => Promise<void>) | null>(null);
  const flowReportChartRendererRef = useRef<ChartImageRenderer | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const hasExportSources = hasRegisteredCharts || pageHasTextOrAiCharts;

  const isPageDataSettled =
    loadingCount === 0 &&
    unregisteredPendingCount === 0 &&
    pptAuxiliaryBlockingCount === 0;
  const isPageDataReady = hasExportSources && isPageDataSettled;

  const isPageDataSettledRef = useRef(isPageDataSettled);
  isPageDataSettledRef.current = isPageDataSettled;
  const readyWaitersRef = useRef<Set<() => void>>(new Set());

  useEffect(() => {
    if (!isPageDataSettled) return;
    const waiters = Array.from(readyWaitersRef.current);
    readyWaitersRef.current.clear();
    for (const resolve of waiters) resolve();
  }, [isPageDataSettled]);

  useEffect(() => {
    const waiters = readyWaitersRef.current;
    return () => {
      for (const resolve of Array.from(waiters)) resolve();
    };
  }, []);

  const waitForPageDataReady = useCallback(
    ({ quiet = false }: { quiet?: boolean } = {}) =>
      new Promise<void>((resolve) => {
        if (isPageDataSettledRef.current) {
          resolve();
          return;
        }
        if (!quiet) {
          toast({
            title: 'Preparing PDF',
            description: 'Waiting for every chart to finish loading.',
            status: 'info',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
        const settle = () => {
          window.clearTimeout(timer);
          readyWaitersRef.current.delete(settle);
          resolve();
        };
        const timer = window.setTimeout(() => {
          settle();
          toast({
            title: 'Some charts are still loading',
            description:
              'The export may show them as placeholders or incomplete. Retry once they finish loading.',
            status: 'warning',
            duration: 6000,
            isClosable: true,
            position: 'top-right',
          });
        }, PAGE_DATA_READY_TIMEOUT_MS);
        readyWaitersRef.current.add(settle);
      }),
    [toast]
  );

  const recomputeReadinessCounts = () => {
    const nextLoadingCount = Array.from(chartRegistry.current.keys()).reduce(
      (count, id) => {
        if (loadingStateRef.current.get(id) === true) {
          return count + 1;
        }
        return count;
      },
      0
    );
    setLoadingCount(nextLoadingCount);

    const nextUnregisteredPendingCount = Array.from(
      loadingStateRef.current.entries()
    ).reduce((count, [id, isLoading]) => {
      if (isLoading && !chartRegistry.current.has(id)) {
        return count + 1;
      }
      return count;
    }, 0);
    setUnregisteredPendingCount(nextUnregisteredPendingCount);
  };

  const registerChart = (id: string, info: ChartExportInfo) => {
    const prior = chartRegistry.current.get(id);
    chartRegistry.current.set(
      id,
      prior === undefined ? info : mergeChartExportInfo(prior, info)
    );
    setHasRegisteredCharts(true);
    const knownLoading = loadingStateRef.current.get(id);
    loadingStateRef.current.set(id, knownLoading ?? false);
    recomputeReadinessCounts();
  };

  const unregisterChart = (id: string) => {
    chartRegistry.current.delete(id);
    loadingStateRef.current.delete(id);
    recomputeReadinessCounts();
  };

  const updateChartExportInfo = useCallback(
    (id: string, partial: Partial<ChartExportInfo>) => {
      const existing = chartRegistry.current.get(id);
      if (!existing) return;
      chartRegistry.current.set(id, mergeChartExportInfo(existing, partial));
    },
    []
  );

  const clearChartExportInfo = useCallback(
    (id: string, fields: (keyof ChartExportInfo)[]) => {
      const existing = chartRegistry.current.get(id);
      if (!existing) return;
      const next: ChartExportInfo = { ...existing };
      fields.forEach((field) => {
        delete next[field];
      });
      chartRegistry.current.set(id, next);
    },
    []
  );

  const updateChartLoadingState = (id: string, isLoading: boolean) => {
    loadingStateRef.current.set(id, isLoading);
    recomputeReadinessCounts();
  };

  const setPptExportResolver = useCallback(
    (fn: (() => ChartExportInfo[]) | null) => {
      pptExportResolverRef.current = fn;
    },
    []
  );

  const setReportExportMetadata = useCallback(
    (metadata: ReportExportMetadata | null) => {
      reportMetadataRef.current = metadata;
    },
    []
  );

  const setDefaultExportFileName = useCallback((fileName: string) => {
    defaultExportFileNameRef.current = fileName;
  }, []);

  const setFlowReportPrinter = useCallback(
    (printer: (() => Promise<void>) | null) => {
      flowReportPrinterRef.current = printer;
    },
    []
  );

  const setFlowReportChartRenderer = useCallback(
    (renderer: ChartImageRenderer | null) => {
      flowReportChartRendererRef.current = renderer;
    },
    []
  );

  const buildReportPptExport = (
    reportMetadata: ReportExportMetadata,
    charts: ChartExportInfo[],
    cover?: PptCoverInfo
  ) => {
    const { flow, primarySelections, ...metadata } = reportMetadata;
    const shared = {
      ...metadata,
      coverParameters: resolveCoverParameters(cover),
      charts,
      downloadName: defaultExportFileNameRef.current,
    };
    return flow
      ? exportFlowReportToPpt({
          ...shared,
          doc: flow,
          renderChartImages: flowReportChartRendererRef.current,
          primarySelections,
        })
      : exportReportToPpt(shared);
  };

  const printFlowReport = async () => {
    const printer = flowReportPrinterRef.current;
    if (isExporting || !printer) return;

    mixpanel.track(PageNavTrackingEvents.PAGE_DOWNLOAD_STARTED, {
      download_format: 'pdf',
      chart_count: chartRegistry.current.size,
    });

    setIsExporting(true);
    try {
      await printer();
    } catch (e) {
      console.error('Error printing report to PDF:', e);
    } finally {
      setIsExporting(false);
    }
  };

  const getChartRegistrySnapshot = useCallback(
    () => new Map(chartRegistry.current),
    []
  );

  const resolveCoverParameters = (cover?: PptCoverInfo) =>
    mergeCoverParameters(
      reportMetadataRef.current?.coverParameters,
      cover?.parameters
    );

  const resolveChartsForPpt = (): ChartExportInfo[] => {
    const resolver = pptExportResolverRef.current;
    if (resolver) {
      return resolver();
    }
    return Array.from(chartRegistry.current.values());
  };

  const exportAllCharts = async (options?: { cover?: PptCoverInfo }) => {
    if (isExporting) return;
    const charts = resolveChartsForPpt();
    const reportMetadata = reportMetadataRef.current;
    if (charts.length === 0 && !reportMetadata) {
      console.warn('No content registered for export.');
      return;
    }

    mixpanel.track(PageNavTrackingEvents.PAGE_DOWNLOAD_STARTED, {
      download_format: 'ppt',
      chart_count: charts.length,
    });

    try {
      const exportPromise = reportMetadata
        ? buildReportPptExport(reportMetadata, charts, options?.cover).getFile()
        : exportMultipleChartsToPpt({
            charts,
            cover: options?.cover,
            downloadName: defaultExportFileNameRef.current,
          }).getFile();
      showExportToast(toast, exportPromise);
    } catch (e) {
      console.error('Error exporting dashboard:', e);
    }
  };

  const exportAllChartsToPdf = async (options?: { cover?: PdfCoverParams }) => {
    if (isExporting) return;

    const element = pdfExportElementRef.current;
    if (!element) {
      console.warn('No PDF export element registered.');
      return;
    }

    const charts = Array.from(chartRegistry.current.values());
    const fileNameBase = defaultExportFileNameRef.current || 'dashboard-export';

    mixpanel.track(PageNavTrackingEvents.PAGE_DOWNLOAD_STARTED, {
      download_format: 'pdf',
      chart_count: charts.length,
    });

    setIsExporting(true);
    try {
      const exportPromise = exportElementToPdfWithResult({
        element,
        fileNameBase,
        cover: options?.cover,
      }).getFile();
      showExportToast(toast, exportPromise);
      await exportPromise;
    } catch (e) {
      console.error('Error exporting dashboard PDF:', e);
    } finally {
      setIsExporting(false);
    }
  };

  const previewAllCharts = async (options?: {
    cover?: PptCoverInfo;
  }): Promise<UploadFileResult | null> => {
    if (isExporting) return null;
    const charts = resolveChartsForPpt();
    const reportMetadata = reportMetadataRef.current;
    if (charts.length === 0 && !reportMetadata) {
      console.warn('No content registered for export.');
      return null;
    }

    try {
      const uploadPromise = (async () => {
        const result = reportMetadata
          ? await buildReportPptExport(
              reportMetadata,
              charts,
              options?.cover
            ).getBlob()
          : await exportMultipleChartsToPpt({
              charts,
              cover: options?.cover,
              downloadName: defaultExportFileNameRef.current,
            }).getBlob();
        if (!result) {
          throw new Error('Failed to prepare PPT for preview.');
        }
        return await uploadFileToStorage(result);
      })();

      showUploadToast(toast, uploadPromise);
      return await uploadPromise;
    } catch (e) {
      console.error('Error exporting dashboard for preview:', e);
      return null;
    }
  };

  const previewAllChartsPdf = async (options?: {
    cover?: PdfCoverParams;
  }): Promise<string | null> => {
    if (isExporting) return null;

    const element = pdfExportElementRef.current;
    if (!element) {
      console.warn('No PDF export element registered.');
      return null;
    }

    const fileNameBase = defaultExportFileNameRef.current || 'dashboard-export';

    setIsExporting(true);
    try {
      const result = await generatePdfBlob({
        element,
        fileNameBase,
        cover: options?.cover,
      });

      if (!result) throw new Error('Failed to generate PDF');

      const blobUrl = URL.createObjectURL(result.blob);

      return blobUrl;
    } catch (e) {
      console.error('Error exporting dashboard PDF for preview:', e);
      return null;
    } finally {
      setIsExporting(false);
    }
  };

  const exportAllChartsData = (options?: { cover?: PptCoverInfo }) => {
    const charts = Array.from(chartRegistry.current.values());
    if (charts.length === 0) {
      console.warn('No content registered for data export.');
      return;
    }

    mixpanel.track(PageNavTrackingEvents.PAGE_DOWNLOAD_STARTED, {
      download_format: 'data',
      chart_count: charts.length,
    });

    setTimeout(async () => {
      try {
        const sheets: ChartSheetData[] = [];

        for (const chart of charts) {
          try {
            const { data, dataType, title } = chart;

            if (!data || !dataType) {
              console.warn(`No data available for chart: ${title}`);
              continue;
            }

            const converter = dataDownloadRegistry[dataType];
            if (!converter) {
              console.warn(`No converter found for data type: ${dataType}`);
              continue;
            }

            const columnLabels = chart.columnLabels || {
              primary: 'Dimension',
              secondary: 'Secondary Dimension',
              metric: 'Value',
            };

            const { sheetData, skipHeader = false } = converter(
              data,
              columnLabels.primary,
              columnLabels.secondary,
              columnLabels.metric
            );

            if (sheetData.length === 0) {
              console.warn(`No data available for chart: ${title}`);
              continue;
            }

            sheets.push({
              title,
              data: sheetData,
              options: { skipHeader },
            });
          } catch (error) {
            console.error(`Error processing chart ${chart.title}:`, error);
          }
        }

        if (sheets.length > 0) {
          downloadMultiSheetData(
            sheets,
            defaultExportFileNameRef.current || 'report-data',
            {
              pageTitle:
                reportMetadataRef.current?.reportTitle || options?.cover?.title,
              parameters: resolveCoverParameters(options?.cover),
            }
          );
        } else {
          console.warn('No valid data to export');
        }
      } catch (e) {
        console.error('Error exporting dashboard data:', e);
      }
    }, 0);
  };

  return (
    <PageExportContext.Provider
      value={{
        registerChart,
        unregisterChart,
        updateChartExportInfo,
        clearChartExportInfo,
        updateChartLoadingState,
        setPptExportResolver,
        setPptAuxiliaryBlockingCount,
        setPageHasTextOrAiCharts,
        setReportExportMetadata,
        getChartRegistrySnapshot,
        pdfExportRef: pdfExportElementRef,
        setDefaultExportFileName,
        exportAllCharts,
        exportAllChartsToPdf,
        exportAllChartsData,
        previewAllCharts,
        previewAllChartsPdf,
        setFlowReportPrinter,
        printFlowReport,
        setFlowReportChartRenderer,
        isExporting,
        isPageDataReady,
        waitForPageDataReady,
      }}
    >
      {children}
    </PageExportContext.Provider>
  );
};

export const usePageExport = (): PageExportContextType => {
  const ctx = useContext(PageExportContext);
  if (!ctx) {
    throw new Error('usePageExport must be used within a PageExportProvider');
  }
  return ctx;
};
